import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Popover, Menu, MenuItem, MenuDivider, Position, Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import './LeadSelector.css';

interface LeadSelectorProps {
  currentLeadId?: string;
  onLeadSelect: (userId: string | null) => void;
  minimal?: boolean;
  showLabel?: boolean;
}

interface UserProfile {
  id: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
  name?: string;
}

const LeadSelector: React.FC<LeadSelectorProps> = observer(({
  currentLeadId,
  onLeadSelect,
  minimal = false,
  showLabel = true
}) => {
  const [currentLead, setCurrentLead] = useState<UserProfile | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const store = useAppStore();

  // Get users from the UserStore (already filtered to exclude soft-deleted members)
  const userProfiles = React.useMemo(() => {
    if (!store) {
      return [];
    }
    const profiles = store.user.users.map(user => ({
      id: user.id,
      userId: user.id,
      firstName: user.firstName || user.name?.split(' ')[0],
      lastName: user.lastName || user.name?.split(' ')[1],
      email: user.email,
      avatar: user.avatarUrl || user.avatar,
      name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      status: user.status,
    }));
    // Only log if there's an issue (0 profiles is a problem worth noting once)
    if (profiles.length === 0 && store.user.users.length === 0) {
      console.warn('⚠️ [LeadSelector] No user profiles available - store.user.users is empty');
    }
    return profiles;
  }, [store?.user.users]);

  // Find current lead
  useEffect(() => {
    if (currentLeadId && userProfiles.length > 0) {
      const lead = userProfiles.find((p: UserProfile) => 
        p.userId === currentLeadId || p.id === currentLeadId
      );
      setCurrentLead(lead || null);
    } else {
      setCurrentLead(null);
    }
  }, [currentLeadId, userProfiles]);

  const getDisplayName = (profile: UserProfile) => {
    if (profile.firstName || profile.lastName) {
      return `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    }
    return profile.email || 'Unknown User';
  };

  const getInitials = (profile: UserProfile) => {
    const name = getDisplayName(profile);
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const renderAvatar = (profile: UserProfile | null, isPlaceholder = false) => {
    if (isPlaceholder) {
      return (
        <div className="lead-avatar lead-avatar-placeholder">
          <span className="lead-avatar-plus">+</span>
        </div>
      );
    }

    if (!profile) {
      return null;
    }

    if (profile.avatar) {
      return (
        <img 
          src={profile.avatar} 
          alt={getDisplayName(profile)}
          className="lead-avatar"
        />
      );
    }

    return (
      <div className="lead-avatar lead-avatar-initials">
        {getInitials(profile)}
      </div>
    );
  };

  const userMenu = (
    <Menu>
      <MenuItem
        text="No Lead"
        icon={IconNames.CROSS}
        onClick={() => {
          onLeadSelect(null);
          setCurrentLead(null);
          setIsOpen(false);
        }}
      />
      <MenuDivider />
      {userProfiles.map((profile) => (
        <MenuItem
          key={profile.id}
          text={getDisplayName(profile)}
          icon={profile.avatar ? undefined : IconNames.USER}
          labelElement={renderAvatar(profile)}
          onClick={() => {
            onLeadSelect(profile.userId || profile.id);
            setCurrentLead(profile);
            setIsOpen(false);
          }}
          active={currentLead?.id === profile.id}
        />
      ))}
    </Menu>
  );

  if (minimal) {
    // Minimal mode for showing on nodes
    return (
      <Popover
        content={userMenu}
        position={Position.BOTTOM_LEFT}
        isOpen={isOpen}
        onInteraction={(nextOpenState) => {
          setIsOpen(nextOpenState);
        }}
        minimal={true}
        usePortal={true}
        portalContainer={document.body}
        popoverClassName="lead-selector-popover"
        modifiers={{
          preventOverflow: { 
            enabled: true,
            boundariesElement: 'viewport'
          },
          flip: { 
            enabled: true,
            behavior: ['bottom', 'top', 'left', 'right']
          }
        }}
        canEscapeKeyClose={true}
        interactionKind="click"
      >
        <div className="lead-selector-minimal" style={{ cursor: 'pointer' }}>
          {currentLead ? renderAvatar(currentLead) : renderAvatar(null, true)}
        </div>
      </Popover>
    );
  }

  // Full mode for node details panel
  return (
    <div className="lead-selector">
      {showLabel && <label className="lead-selector-label">Lead</label>}
      <Popover
        content={userMenu}
        position={Position.BOTTOM_LEFT}
        isOpen={isOpen}
        onInteraction={(state) => setIsOpen(state)}
        usePortal={true}
        portalContainer={document.body}
        popoverClassName="lead-selector-popover"
        modifiers={{
          preventOverflow: { 
            enabled: true,
            boundariesElement: 'viewport'
          },
          flip: { 
            enabled: true,
            behavior: ['bottom', 'top', 'left', 'right']
          }
        }}
        canEscapeKeyClose={true}
      >
        <Button
          rightIcon={IconNames.CARET_DOWN}
          text={currentLead ? getDisplayName(currentLead) : 'Select Lead...'}
          icon={currentLead ? undefined : IconNames.USER}
          className="lead-selector-button"
        />
      </Popover>
    </div>
  );
});

LeadSelector.displayName = 'LeadSelector';

export default LeadSelector;
